/* eslint-disable no-console */
import { getMessaging, getToken, onMessage } from 'firebase/messaging';
import { initializeApp, getApp } from 'firebase/app';
import type { FirebaseApp } from 'firebase/app';

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
};

// Ensure we only initialize Firebase app once
let app: FirebaseApp;

try {
  // Try to get existing app first
  app = getApp();
} catch {
  // If no app exists, initialize it
  app = initializeApp(firebaseConfig);
}

// Lazy initialization of messaging to avoid errors in unsupported contexts
let messaging: ReturnType<typeof getMessaging> | null = null;

const getMessagingInstance = () => {
  if (!messaging) {
    try {
      messaging = getMessaging(app);
    } catch (error) {
      console.warn('Firebase messaging not supported in this context:', error);
      return null;
    }
  }
  return messaging;
};

const FCM_TOKEN_STORAGE_KEY = 'fcm_token';

/** Session guard so we re-register after logout; cleared on logout. */
export const FCM_WEB_REGISTRATION_SESSION_KEY = 'fcm_web_registration';

export const getSavedFcmToken = (): string | null => {
  try {
    return localStorage.getItem(FCM_TOKEN_STORAGE_KEY);
  } catch {
    return null;
  }
};

export const setSavedFcmToken = (token: string) => {
  try {
    localStorage.setItem(FCM_TOKEN_STORAGE_KEY, token);
  } catch {
    // ignore storage errors
  }
};

export const clearSavedFcmToken = () => {
  try {
    localStorage.removeItem(FCM_TOKEN_STORAGE_KEY);
  } catch {
    // ignore storage errors
  }
};

export const requestFCMToken = async () => {
  try {
    if (typeof window === 'undefined' || !('Notification' in window)) {
      return null;
    }
    if (Notification.permission === 'denied') {
      return null;
    }

    const messagingInstance = getMessagingInstance();
    if (!messagingInstance) {
      console.warn('Firebase messaging not available in this context');
      return null;
    }

    let permission: NotificationPermission = Notification.permission;
    if (permission === 'default') {
      permission = await Notification.requestPermission();
    }
    if (permission !== 'granted') {
      console.log('Notification permission not granted.');
      return null;
    }

    const registration = await navigator.serviceWorker.register(
      '/firebase-messaging-sw.js',
      {
        type: 'module',
      }
    );

    const token = await getToken(messagingInstance, {
      vapidKey: import.meta.env.VITE_FIREBASE_VAPID_KEY,
      serviceWorkerRegistration: registration,
    });

    if (token) setSavedFcmToken(token);
    return token;
  } catch (err) {
    console.error('An error occurred while retrieving token: ', err);
    return null;
  }
};

let isListenerInitialized = false;

export const listenForMessages = () => {
  console.log(
    '🔍 listenForMessages called, isListenerInitialized:',
    isListenerInitialized
  );

  if (isListenerInitialized) {
    console.log(
      '📩 Firebase message listener already initialized, skipping...'
    );
    return;
  }

  const messagingInstance = getMessagingInstance();
  if (!messagingInstance) {
    console.warn(
      'Firebase messaging not available, skipping listener initialization'
    );
    return;
  }

  console.log('🚀 Initializing Firebase message listener...');
  isListenerInitialized = true;

  onMessage(messagingInstance, payload => {
    console.log('📩 Foreground message:', payload);
    const data = payload.data ?? {};
    const title =
      payload.notification?.title ??
      (typeof data.title === 'string' ? data.title : null) ??
      'Itria';
    const body =
      payload.notification?.body ??
      (typeof data.body === 'string' ? data.body : '') ??
      '';
    if (title || body) {
      new Notification(title, {
        body: body || undefined,
        icon: '/logo.png',
        data,
      });
    }
  });

  console.log('✅ Firebase message listener initialized successfully');
};
